'use client';

import { Box } from '@mui/material';
import { useMemo } from 'react';
import { CircularProgress, Link, Typography } from '@/primitives';
import { useEbvTraitExplanations } from '@/components/publicSearch/animal/ebv/useEbvTraitExplanations';
import type { Society } from '@/publicSearch/store/usePublicSearchContext';
import { useAnimalEbvData, type AnimalEbvSource } from './useAnimalEbvData';
import { useCurrencySymbol } from '@/hooks/publicSearch/useCurrencySymbol';
import { useEbvDisplayOptions } from '@/hooks/publicSearch/useEbvDisplayOptions';
import { formatEbvPluralLabel } from '@/components/publicSearch/animal/ebv/ebvProviderCapabilities';
import { ebvGroupingOptionsFromDisplay } from '@/hooks/publicSearch/ebv/ebvColumns';
import {
  resolveDetailAvgCaptions,
  resolveGraphFooterCaption,
  resolveRunLabel,
} from '@/components/publicSearch/animal/ebv/breedAverageLabels';
import { Ilr2MixedHtmlLabel } from '@/components/publicSearch/Ilr2MixedHtmlLabel';
import Ilr2CaptionText from '@/components/publicSearch/animal/ebv/Ilr2CaptionText';
import { parseMixedIlr2Html } from '@/utils/stripHtmlForTooltip';
import BreedplanInterpretationInfoTrigger from '@/components/publicSearch/animal/ebv/BreedplanInterpretationInfoTrigger';
import { ROUTE_SEGMENT } from '@/constants/routeSegments';
import { BREEDPLAN_ACCURACY_URL } from './animalEbvTabUtils';
import AnimalEbvTabTraitTable from './AnimalEbvTabTraitTable';
import AnimalEbvTabBreedAverageTable from './AnimalEbvTabBreedAverageTable';
import AnimalEbvTabSelectionIndexTable from './AnimalEbvTabSelectionIndexTable';
import AnimalEbvTabCharts from './AnimalEbvTabCharts';
import { formatEbvStatisticsLine } from './formatEbvStatisticsLine';

type AnimalEbvTabProps = {
  society: Society;
  animalData: AnimalEbvSource;
  isLoading: boolean;
  isMobile: boolean;
  societyCode: string | undefined;
  animalIdFromRoute: string;
};

/**
 * EBV tab body for the public animal-detail page: the trait EBV table, breed
 * average table, selection-index table and the percentile / potential-change
 * charts. Data shaping lives in {@link useAnimalEbvData}; this component owns
 * only the EBV-specific UI state (chart view + info popovers).
 */
export default function AnimalEbvTab({
  society,
  animalData,
  isLoading,
  isMobile,
  societyCode,
  animalIdFromRoute,
}: AnimalEbvTabProps) {
  const {
    ebvAverageData,
    ebvAverageMeta,
    ebvRows,
    selectionIndexColumns,
    ebvChartData,
  } = useAnimalEbvData(society, animalData);
  const currencySymbol = useCurrencySymbol(society);
  const {
    ebvLabel,
    showAccuracy,
    showBreedAverages,
    showBreedplanHelp,
    showPercentileGraph,
    showPercentileSearch,
    showPotentialChangeGraph,
    showRowLabels,
    showTraitsObserved,
    isBlackRedAngus,
    useColourSlotGrouping,
    raw: ebvDisplayRaw,
  } = useEbvDisplayOptions();
  const {
    traitExplanationsByKey,
    traitHeaderContext,
    popoverElement: ebvTraitExplanationPopover,
  } = useEbvTraitExplanations(society.settings, showBreedplanHelp);

  const percentileGraphTitle = `${ebvLabel} Percentile Graph`;
  const potentialChangeGraphTitle = `${ebvLabel} Potential Change Graph`;
  const showAnyChart =
    ebvChartData.length > 0 &&
    (showPercentileGraph || showPotentialChangeGraph);

  const traitsObservedRaw = animalData?.ebvRaw?.traitsObserved?.trim();
  const traitsObservedParts = parseMixedIlr2Html(traitsObservedRaw);
  const hasTraitsObservedContent =
    Boolean(traitsObservedParts.plainText) ||
    Boolean(traitsObservedParts.imgMarkup && societyCode);
  const statisticsLine = formatEbvStatisticsLine(
    {
      numberOfHerds: animalData?.ebvRaw?.numberOfHerds,
      progenyCount: animalData?.ebvRaw?.progenyCount,
      scanProgenyCount: animalData?.ebvRaw?.scanProgenyCount,
      carcaseProgenyCount: animalData?.ebvRaw?.carcaseProgenyCount,
      daughtersCount: animalData?.ebvRaw?.daughtersCount,
    },
    ebvDisplayRaw?.ebvBoxOptionsRaw,
  );

  const runLabel = resolveRunLabel(
    animalData?.ebvRaw?.ebvDescription as string | undefined,
    ebvAverageMeta.runLabel,
  );
  const ebvGroupingOptions = useMemo(
    () => ebvGroupingOptionsFromDisplay({ useColourSlotGrouping }),
    [useColourSlotGrouping],
  );
  const effectiveEbvAverageData = showBreedAverages ? ebvAverageData : null;
  const detailAvgCaptions = resolveDetailAvgCaptions(
    isBlackRedAngus,
    effectiveEbvAverageData,
    showPercentileSearch,
  );
  const graphFooterCaption = resolveGraphFooterCaption(effectiveEbvAverageData);
  const showPercentileColumn = showPercentileSearch;
  // Phones fold the breed average into the trait table; wider screens keep
  // the separate summary row below it (ILRX-4700).
  const showBreedAverageColumn = isMobile && Boolean(effectiveEbvAverageData);
  const showAccuracyColumn = showAccuracy;
  const ebvPluralLabel = formatEbvPluralLabel(ebvLabel);
  const returnTo = encodeURIComponent(
    `/${societyCode}/${ROUTE_SEGMENT.ANIMALS}/${animalIdFromRoute}`,
  );
  const percentileLink = (band: 'B' | 'R') =>
    `/${societyCode}/${ROUTE_SEGMENT.ANIMALS}/ebv-percentages?band=${band}&returnTo=${returnTo}`;
  const percentileLinkGeneral = `/${societyCode}/${ROUTE_SEGMENT.ANIMALS}/ebv-percentages?returnTo=${returnTo}`;

  const accuracyHeaderText = isMobile ? 'Acc.' : 'Accuracy';
  const accuracyLabel = (
    <Typography
      variant="bodysmall"
      sx={{
        color: 'text.secondary',
      }}
    >
      {accuracyHeaderText}
    </Typography>
  );
  const accuracyHeader = showBreedplanHelp ? (
    <Link
      href={BREEDPLAN_ACCURACY_URL}
      target="_blank"
      rel="noopener noreferrer"
      variant="bodysmall"
      sx={{
        color: 'text.secondary',
      }}
    >
      {accuracyHeaderText}
    </Link>
  ) : (
    accuracyLabel
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={16} />
        <Typography
          variant="bodysmall"
          sx={{
            color: 'text.secondary',
          }}
        >
          Loading {ebvPluralLabel}…
        </Typography>
      </Box>
    );
  }

  if (ebvRows.length === 0) {
    return (
      <Typography
        variant="bodysmall"
        sx={{
          color: 'text.secondary',
        }}
      >
        No {ebvPluralLabel} available.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box>
        {!!runLabel && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 0.5,
              mb: 1.5,
            }}
          >
            <Typography sx={{ fontWeight: 600, textAlign: 'center' }}>
              {runLabel}
            </Typography>
            <BreedplanInterpretationInfoTrigger
              show={showBreedplanHelp}
              dataDdActionName="Animal EBV Tab: BREEDPLAN Interpretation Info"
            />
          </Box>
        )}

        <Box
          sx={{
            backgroundColor: 'background.paper',
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            overflowX: 'auto',
          }}
        >
          <AnimalEbvTabTraitTable
            isMobile={isMobile}
            ebvRows={ebvRows}
            showPercentileColumn={showPercentileColumn}
            showAccuracyColumn={showAccuracyColumn}
            showRowLabels={showRowLabels}
            ebvLabel={ebvLabel}
            accuracyHeader={accuracyHeader}
            showBreedAverageColumn={showBreedAverageColumn}
            ebvAverageData={effectiveEbvAverageData}
            currencySymbol={currencySymbol}
            traitHeaderContext={traitHeaderContext}
            ebvGroupingOptions={ebvGroupingOptions}
          />
        </Box>

        {detailAvgCaptions.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 1.5,
              mt: 2,
            }}
          >
            {detailAvgCaptions.map((line) => (
              <Box
                key={line.band ?? line.caption}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 1.5,
                  flexWrap: 'wrap',
                }}
              >
                <Typography sx={{ textAlign: 'center', fontWeight: 600 }}>
                  <Ilr2CaptionText raw={line.caption} />
                </Typography>
                {showPercentileSearch && line.band === 'B' && (
                  <Link
                    href={percentileLink('B')}
                    sx={{ fontWeight: 500, textDecoration: 'none' }}
                  >
                    View Black Percentiles
                  </Link>
                )}
                {showPercentileSearch && line.band === 'R' && (
                  <Link
                    href={percentileLink('R')}
                    sx={{ fontWeight: 500, textDecoration: 'none' }}
                  >
                    View Red Percentiles
                  </Link>
                )}
                {showPercentileSearch && !line.band && (
                  <Link
                    href={percentileLinkGeneral}
                    sx={{ fontWeight: 500, textDecoration: 'none' }}
                  >
                    View Percentiles
                  </Link>
                )}
              </Box>
            ))}
            {!runLabel && (
              <BreedplanInterpretationInfoTrigger
                show={showBreedplanHelp}
                dataDdActionName="Animal EBV Tab: BREEDPLAN Interpretation Info"
              />
            )}
          </Box>
        )}

        {effectiveEbvAverageData && !showBreedAverageColumn && (
          <Box
            sx={{
              mt: 2,
              backgroundColor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              overflowX: 'auto',
            }}
          >
            <AnimalEbvTabBreedAverageTable
              isMobile={isMobile}
              ebvRows={ebvRows}
              ebvAverageData={effectiveEbvAverageData}
              ebvLabel={ebvLabel}
              currencySymbol={currencySymbol}
              traitHeaderContext={traitHeaderContext}
              ebvGroupingOptions={ebvGroupingOptions}
            />
          </Box>
        )}
      </Box>
      {((showTraitsObserved && hasTraitsObservedContent) || statisticsLine) && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0,
          }}
        >
          {showTraitsObserved && hasTraitsObservedContent && (
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                justifyContent: 'center',
                flexWrap: 'wrap',
                alignItems: 'center',
              }}
            >
              <Typography sx={{ fontWeight: 600 }}>Traits Analysed:</Typography>
              <Ilr2MixedHtmlLabel
                raw={traitsObservedRaw}
                societyId={society.orgId}
                badgeAlt="Traits analysed badge"
              >
                {(plain) => (
                  <Typography
                    component="span"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    {plain}
                  </Typography>
                )}
              </Ilr2MixedHtmlLabel>
            </Box>
          )}
          {statisticsLine && (
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                justifyContent: 'center',
                flexWrap: 'wrap',
                alignItems: 'center',
              }}
            >
              <Typography sx={{ fontWeight: 600 }}>
                {statisticsLine.label}
              </Typography>
              <Typography
                component="span"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {statisticsLine.body}
              </Typography>
            </Box>
          )}
        </Box>
      )}
      {selectionIndexColumns.length > 0 && (
        <AnimalEbvTabSelectionIndexTable
          isMobile={isMobile}
          selectionIndexColumns={selectionIndexColumns}
          showPercentileColumn={showPercentileColumn}
          ebvAverageData={effectiveEbvAverageData}
          currencySymbol={currencySymbol}
          traitHeaderContext={traitHeaderContext}
        />
      )}
      {showAnyChart && (
        <AnimalEbvTabCharts
          showPercentileGraph={showPercentileGraph}
          showPotentialChangeGraph={showPotentialChangeGraph}
          ebvChartData={ebvChartData}
          percentileGraphTitle={percentileGraphTitle}
          potentialChangeGraphTitle={potentialChangeGraphTitle}
          ebvLabel={ebvLabel}
          breedAverageMessage={graphFooterCaption ?? ''}
          traitExplanationsByKey={traitExplanationsByKey}
          showBreedplanHelp={showBreedplanHelp}
          useColourSlotGrouping={useColourSlotGrouping}
        />
      )}
      {ebvTraitExplanationPopover}
    </Box>
  );
}
